import { find } from 'lodash';
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { Button, MessagePlugin, Space } from 'tdesign-react';
import Descriptions from '@/components/Descriptions';
import PermWrapper from '@/components/PermWrapper';
import ShadowCard from '@/components/ShadowCard';
import SvgIcon from '@/components/SvgIcon';
import { sendExecOperate } from '@/services/execRecordService';
import {
  clusterTypeOptions,
  executeResultOptions,
  jobOptions,
  processStatusOptions,
} from '@/utils/constant';

function Summary({ info, id, executeRefresh }) {
  const execResultCode = Number(info.execResultCode);
  const [searchParams] = useSearchParams();
  const clusterId = searchParams.get('clusterId');

  const execDirective = async (directive) => {
    let msg;
    try {
      msg = MessagePlugin.loading({
        duration: 0,
        content: '正在执行...',
      });
      await sendExecOperate({ id, status: directive, clusterId });
      MessagePlugin.success('执行成功！');
      executeRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      MessagePlugin.close(msg);
    }
  };

  const result = useMemo(() => {
    const found = find(executeResultOptions || [], { value: execResultCode }) || {};
    let icon;

    switch (found.value) {
      case 0: // 执行中
        icon = <SvgIcon type='solid-clock' color='var(--td-brand-color)' />;
        break;
      case 1: // 执行失败
        icon = <SvgIcon type='solid-circle-x' color='var(--td-error-color)' />;
        break;
      case 2: // 执行成功
        icon = <SvgIcon type='solid-circle-check' color='var(--td-success-color)' />;
        break;
      case 3: // 执行终止
        icon = <SvgIcon type='solid-circle-x' color='var(--td-warning-color)' />;
        break;
      default:
        break;
    }

    return (
      <Space size={2}>
        {icon}
        {found.label}
      </Space>
    );
  }, [execResultCode]);

  return (
    <ShadowCard bodyStyle={{ paddingBottom: 10 }} style={{ marginBottom: 16 }}>
      <Descriptions column={4} title='任务详情'>
        <Descriptions.Item label='任务ID' value={info.id} />
        <Descriptions.Item
          label='任务类型'
          value={info.stencilType}
          type='options'
          options={jobOptions}
        />
        <Descriptions.Item label='任务名称' value={info.taskName} />
        <Descriptions.Item
          label='流程类型'
          value={info.flowType}
          type='options'
          options={clusterTypeOptions}
        />
        {info.flowType === 1 && <Descriptions.Item label='k8s集群名称' value={info.kubeName} />}
        <Descriptions.Item label='任务描述' value={info.taskNote} />
        <Descriptions.Item
          label='任务状态'
          value={info.status}
          type='options'
          options={processStatusOptions}
        />
        <Descriptions.Item label='执行结果' value={result} />
        <Descriptions.Item label='开始时间' value={info.beginTime} type='datetime' />
        <Descriptions.Item label='结束时间' value={info.endTime} type='datetime' />
      </Descriptions>
      <OperateLine>
        <Space size='small'>
          {execResultCode === 0 && (
            <PermWrapper resource='pulsar:exec:log:stopTask'>
              <Button
                theme='danger'
                variant='outline'
                onClick={() => execDirective(2)}
                icon={<SvgIcon style={{ marginRight: 4 }} type='ban' />}
              >
                终止任务
              </Button>
            </PermWrapper>
          )}
          {execResultCode === 1 && (
            <>
              <PermWrapper resource='pulsar:exec:log:retry'>
                <Button onClick={() => execDirective(3)}>重试失败节点</Button>
              </PermWrapper>
              <PermWrapper resource='pulsar:exec:log:continue'>
                <Button onClick={() => execDirective(4)}>重试失败节点并继续后续流程</Button>
              </PermWrapper>
            </>
          )}
          {(execResultCode === 3 || (execResultCode === 2 && info.status === 3)) && (
            <PermWrapper resource='pulsar:exec:log:continue'>
              <Button onClick={() => execDirective(4)}>继续后续流程</Button>
            </PermWrapper>
          )}
        </Space>
      </OperateLine>
    </ShadowCard>
  );
}

export default connect((state) => ({
  info: state.execRecord.summary,
}))(Summary);

const OperateLine = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;
